/**
 * ChannelCreditsTab — coach credit-distribution surface for a channel (D3, B-2026-0065).
 *
 * Visible to the channel owner + platform admin. Renders:
 *  - Pool summary card (included vs purchased credits) from GET /token-balance.
 *  - Allocations table (recipient, granted, consumed, remaining, status, expiry)
 *    with a per-row revoke action → ConfirmationDialog → DELETE.
 *  - "Allocate credits" modal → POST /token-allocations.
 *  - Coach caps section (owner + multi-coach channels) → PUT /coach-credit-caps.
 *  - Admin-only "Deposit credits" (POST /token-grant) and "Set tier"
 *    (PATCH /admin/channels/{id}).
 *
 * Graceful degradation: if the balance endpoint 404s (or the credit system is
 * not enabled for this channel) the component shows a quiet note and never
 * crashes — the allocations table and actions simply stay empty.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { isAxiosError } from 'axios';
import Modal from '@/components/ui/Modal';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import Button from '@/components/Button';
import Input from '@/components/ui/Input';
import {
  getTokenBalance,
  getTokenAllocations,
  createTokenAllocation,
  revokeTokenAllocation,
  listCoachCreditCaps,
  setCoachCreditCap,
  depositCredits,
} from '@/services/channelApi';
import { patchAdminChannel } from '@/services/adminApi';
import type {
  ChannelTokenBalance,
  CreditAllocation,
  CoachCreditCap,
} from '@/types/api';
import type { Channel, ChannelMember } from '@/types/channel';

// ─── Props ─────────────────────────────────────────────────────────────────────

interface ChannelCreditsTabProps {
  slug: string;
  channel: Channel;
  members: ChannelMember[];
  isOwner: boolean;
  isAdmin: boolean;
  onToast: (message: string, type?: 'success' | 'error') => void;
}

// ─── Small helpers ───────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-dark-card)',
  border: '1px solid var(--color-border-subtle)',
  borderRadius: '16px',
  padding: '20px 24px',
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  margin: '0 0 12px',
};

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
  borderBottom: '1px solid var(--color-border-subtle)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '9px 12px',
  fontSize: '13px',
  color: 'var(--color-text-secondary)',
  borderBottom: '1px solid var(--color-border-subtle)',
  verticalAlign: 'middle',
};

/** A coach-level member is anyone who can hold an allocation cap. */
function isCoachLevel(m: ChannelMember): boolean {
  const role = (m.member_role ?? m.role) as string;
  return (
    role === 'coach' ||
    role === 'channel_coach' ||
    role === 'channel_admin' ||
    role === 'owner' ||
    m.access_level === 'owner'
  );
}

function memberLabel(m: ChannelMember): string {
  return m.display_name ?? m.email ?? m.user_id;
}

const STATUS_STYLES: Record<string, { color: string; bg: string }> = {
  active: { color: '#34D399', bg: 'rgba(52,211,153,0.12)' },
  exhausted: { color: 'var(--color-text-muted)', bg: 'rgba(168,180,199,0.10)' },
  revoked: { color: '#F87171', bg: 'rgba(248,113,113,0.12)' },
  expired: { color: 'var(--color-accent-gold)', bg: 'rgba(244,201,93,0.12)' },
};

function StatusChip({ status }: { status: string }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.exhausted;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 600,
        color: s.color,
        backgroundColor: s.bg,
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
}

function formatExpiry(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Never';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function ChannelCreditsTab({
  slug,
  channel,
  members,
  isOwner,
  isAdmin,
  onToast,
}: ChannelCreditsTabProps) {
  const [balance, setBalance] = useState<ChannelTokenBalance | null>(null);
  const [allocations, setAllocations] = useState<CreditAllocation[]>([]);
  const [caps, setCaps] = useState<CoachCreditCap[]>([]);
  const [loading, setLoading] = useState(true);
  const [notEnabled, setNotEnabled] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Modal state
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showTierModal, setShowTierModal] = useState(false);
  const [capEditCoach, setCapEditCoach] = useState<ChannelMember | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<CreditAllocation | null>(null);
  const [revoking, setRevoking] = useState(false);

  // Member lookup for rendering recipient names.
  const memberById = useMemo(() => {
    const map = new Map<string, ChannelMember>();
    for (const m of members) map.set(m.user_id, m);
    return map;
  }, [members]);

  const coachMembers = useMemo(() => members.filter(isCoachLevel), [members]);
  // Coach caps section only when the owner is viewing AND there is more than one coach.
  const showCapsSection = isOwner && coachMembers.length > 1;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setNotEnabled(false);
    try {
      const bal = await getTokenBalance(slug);
      setBalance(bal);
      // Allocations + caps are best-effort; a failure here should not blank the pool card.
      const [allocs, capRows] = await Promise.all([
        getTokenAllocations(slug).catch(() => [] as CreditAllocation[]),
        listCoachCreditCaps(slug).catch(() => [] as CoachCreditCap[]),
      ]);
      setAllocations(allocs);
      setCaps(capRows);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        // Channel/pool not found → credit system not enabled for this channel.
        setNotEnabled(true);
      } else {
        setLoadError('Failed to load credit information.');
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      // Partly-consumed allocations require force=true to void the unspent portion.
      const force = revokeTarget.credits_consumed > 0;
      await revokeTokenAllocation(slug, revokeTarget.allocation_id, force);
      onToast('Allocation revoked.');
      setRevokeTarget(null);
      await load();
    } catch {
      onToast('Failed to revoke allocation.', 'error');
    } finally {
      setRevoking(false);
    }
  };

  // ── Not-enabled / error states ──────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '14px' }}>
        Loading credits…
      </div>
    );
  }

  if (notEnabled) {
    return (
      <div style={{ ...cardStyle, color: 'var(--color-text-muted)', fontSize: '14px' }}>
        Credit system not enabled for this channel.
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ ...cardStyle, color: 'var(--color-text-muted)', fontSize: '14px' }}>
        {loadError}
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Pool summary card */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '16px', color: 'var(--color-text-primary)', margin: 0 }}>
            Credit pool
          </h3>
          {channel.subscription_tier != null && (
            <span
              style={{
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: '999px',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--color-accent-blue)',
                backgroundColor: 'rgba(61,169,252,0.12)',
                border: '1px solid rgba(61,169,252,0.30)',
              }}
            >
              Tier {channel.subscription_tier}
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px' }}>
          <PoolStat
            label="Available"
            value={balance?.available_credits ?? 0}
            hint="drawable now"
          />
          <PoolStat
            label="Reserved"
            value={balance?.reserved_credits ?? 0}
            hint="held for in-flight evals"
          />
          <PoolStat
            label="Active granted"
            value={balance?.active_granted ?? 0}
            hint="allocated to members"
          />
          <PoolStat
            label="Consumed"
            value={balance?.total_consumed ?? 0}
            hint="spent this cycle"
          />
        </div>
        <p style={{ margin: '14px 0 0', fontSize: '12px', color: 'var(--color-text-muted)' }}>
          Included (subscription) credits reset monthly. Purchased credits roll over.
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '18px', flexWrap: 'wrap' }}>
          <Button size="sm" onClick={() => setShowAllocateModal(true)}>
            Allocate credits
          </Button>
          {isAdmin && (
            <>
              <Button size="sm" variant="secondary" onClick={() => setShowDepositModal(true)}>
                Deposit credits
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setShowTierModal(true)}>
                Set tier
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Allocations table */}
      <div style={cardStyle}>
        <p style={sectionLabelStyle}>Allocations</p>
        {allocations.length === 0 ? (
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', fontStyle: 'italic', margin: 0 }}>
            No allocations yet.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Recipient</th>
                  <th style={thStyle}>Granted</th>
                  <th style={thStyle}>Consumed</th>
                  <th style={thStyle}>Remaining</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Expiry</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allocations.map((a) => {
                  const recipient = a.recipient_user_id
                    ? memberById.get(a.recipient_user_id)
                    : null;
                  const recipientName = a.recipient_user_id
                    ? recipient
                      ? memberLabel(recipient)
                      : a.recipient_user_id.slice(0, 8) + '…'
                    : 'All members';
                  return (
                    <tr key={a.allocation_id}>
                      <td style={{ ...tdStyle, color: 'var(--color-text-primary)', fontWeight: 500 }}>
                        {recipientName}
                        {!a.recipient_user_id && (
                          <span style={{ marginLeft: '6px', fontSize: '11px', color: 'var(--color-text-muted)', fontWeight: 400 }}>
                            (broadcast)
                          </span>
                        )}
                      </td>
                      <td style={tdStyle}>{a.credits_granted}</td>
                      <td style={tdStyle}>{a.credits_consumed}</td>
                      <td style={tdStyle}>{a.credits_remaining}</td>
                      <td style={tdStyle}><StatusChip status={a.status} /></td>
                      <td style={tdStyle}>{formatExpiry(a.expires_at)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {a.status === 'active' ? (
                          <button
                            type="button"
                            onClick={() => setRevokeTarget(a)}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              border: '1px solid rgba(248,113,113,0.3)',
                              backgroundColor: 'transparent',
                              color: '#F87171',
                              fontSize: '12px',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Revoke
                          </button>
                        ) : (
                          <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Coach caps section */}
      {showCapsSection && (
        <div style={cardStyle}>
          <p style={sectionLabelStyle}>Coach allocation caps</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '480px' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Coach</th>
                  <th style={thStyle}>Cap</th>
                  <th style={thStyle}>Used</th>
                  <th style={thStyle}>Remaining</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coachMembers.map((m) => {
                  const cap = caps.find((c) => c.coach_user_id === m.user_id);
                  return (
                    <tr key={m.user_id}>
                      <td style={{ ...tdStyle, color: 'var(--color-text-primary)', fontWeight: 500 }}>
                        {memberLabel(m)}
                      </td>
                      <td style={tdStyle}>{cap ? cap.allocation_cap : <span style={{ color: 'var(--color-text-muted)' }}>Not set</span>}</td>
                      <td style={tdStyle}>{cap ? cap.allocated_used : '—'}</td>
                      <td style={tdStyle}>{cap ? cap.allocation_remaining : '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => setCapEditCoach(m)}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            border: '1px solid var(--color-border-subtle)',
                            backgroundColor: 'transparent',
                            color: 'var(--color-text-secondary)',
                            fontSize: '12px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {cap ? 'Edit cap' : 'Set cap'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAllocateModal && (
        <AllocateModal
          slug={slug}
          members={members}
          onClose={() => setShowAllocateModal(false)}
          onDone={async () => {
            setShowAllocateModal(false);
            onToast('Credits allocated.');
            await load();
          }}
          onError={(msg) => onToast(msg, 'error')}
        />
      )}

      {isAdmin && showDepositModal && (
        <DepositModal
          slug={slug}
          onClose={() => setShowDepositModal(false)}
          onDone={async () => {
            setShowDepositModal(false);
            onToast('Credits deposited into the pool.');
            await load();
          }}
          onError={(msg) => onToast(msg, 'error')}
        />
      )}

      {isAdmin && showTierModal && (
        <SetTierModal
          channel={channel}
          onClose={() => setShowTierModal(false)}
          onDone={() => {
            setShowTierModal(false);
            onToast('Subscription tier updated.');
          }}
          onError={(msg) => onToast(msg, 'error')}
        />
      )}

      {capEditCoach && (
        <CoachCapModal
          slug={slug}
          coach={capEditCoach}
          currentCap={caps.find((c) => c.coach_user_id === capEditCoach.user_id) ?? null}
          onClose={() => setCapEditCoach(null)}
          onDone={async () => {
            setCapEditCoach(null);
            onToast('Coach cap updated.');
            await load();
          }}
          onError={(msg) => onToast(msg, 'error')}
        />
      )}

      {/* Revoke confirmation */}
      <ConfirmationDialog
        isOpen={revokeTarget != null}
        onClose={() => { if (!revoking) setRevokeTarget(null); }}
        onConfirm={handleConfirmRevoke}
        title="Revoke allocation"
        description={
          revokeTarget
            ? `Void the ${revokeTarget.credits_remaining} unspent credit(s) in this allocation? Consumed credits are not refundable.`
            : ''
        }
        confirmLabel="Revoke"
        variant="danger"
        isLoading={revoking}
      />
    </div>
  );
}

// ─── Pool stat cell ─────────────────────────────────────────────────────────

function PoolStat({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: '12px',
        backgroundColor: 'var(--color-dark-elevated-2)',
        border: '1px solid var(--color-border-subtle)',
      }}
    >
      <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', lineHeight: 1.2, marginTop: '4px' }}>
        {value.toLocaleString()}
      </div>
      <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '2px' }}>{hint}</div>
    </div>
  );
}

// ─── Allocate modal ────────────────────────────────────────────────────────────

interface AllocateModalProps {
  slug: string;
  members: ChannelMember[];
  onClose: () => void;
  onDone: () => void;
  onError: (msg: string) => void;
}

function AllocateModal({ slug, members, onClose, onDone, onError }: AllocateModalProps) {
  const [broadcast, setBroadcast] = useState(false);
  const [recipientId, setRecipientId] = useState('');
  const [amount, setAmount] = useState('');
  const [expiry, setExpiry] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only approved members are eligible recipients. ChannelMembers returned by the
  // members endpoint are already approved; filter defensively on status if present.
  const eligible = useMemo(
    () =>
      members.filter((m) => {
        const status = (m as { status?: string }).status;
        return status == null || status === 'approved' || status === 'active';
      }),
    [members],
  );

  const handleSubmit = async () => {
    setError(null);
    const n = parseInt(amount, 10);
    if (isNaN(n) || n <= 0) {
      setError('Enter an amount greater than 0.');
      return;
    }
    if (!broadcast && !recipientId) {
      setError('Choose a recipient, or check "All current members".');
      return;
    }
    setSaving(true);
    try {
      await createTokenAllocation(slug, {
        recipient_user_id: broadcast ? null : recipientId,
        credits_granted: n,
        expires_at: expiry ? new Date(expiry).toISOString() : null,
      });
      onDone();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: { message?: string } | string } } })?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : (detail as { message?: string })?.message ?? 'Failed to allocate credits.';
      onError(msg);
      setError(msg);
      setSaving(false);
    }
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
    marginBottom: '4px',
  };
  const selectStyle: React.CSSProperties = {
    width: '100%',
    height: '40px',
    borderRadius: '10px',
    border: '1px solid var(--color-border-subtle)',
    backgroundColor: 'var(--color-dark-elevated-2)',
    color: 'var(--color-text-primary)',
    padding: '0 12px',
    fontSize: '14px',
    outline: 'none',
  };

  return (
    <Modal isOpen onClose={onClose} title="Allocate credits" size="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error && (
          <div style={{ padding: '10px 14px', borderRadius: '8px', backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', color: '#F87171', fontSize: '13px' }}>
            {error}
          </div>
        )}

        {/* Broadcast toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={broadcast}
            onChange={(e) => setBroadcast(e.target.checked)}
          />
          <span style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
            All current members (broadcast)
          </span>
        </label>

        {/* Recipient selector (hidden when broadcasting) */}
        {!broadcast && (
          <div>
            <label style={labelStyle} htmlFor="alloc-recipient">Recipient</label>
            <select
              id="alloc-recipient"
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value)}
              style={selectStyle}
            >
              <option value="">Select a member…</option>
              {eligible.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {memberLabel(m)} ({(m.member_role ?? m.role) as string})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Amount */}
        <Input
          label="Amount (credits)"
          type="number"
          min={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="e.g. 10"
        />

        {/* Expiry */}
        <div>
          <label style={labelStyle} htmlFor="alloc-expiry">Expiry (optional)</label>
          <input
            id="alloc-expiry"
            type="date"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            style={selectStyle}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Allocating…' : 'Allocate'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Deposit modal (admin) ──────────────────────────────────────────────────────

interface DepositModalProps {
  slug: string;
  onClose: () => void;
  onDone: () => void;
  onError: (msg: string) => void;
}

function DepositModal({ slug, onClose, onDone, onError }: DepositModalProps) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    const n = parseInt(amount, 10);
    if (isNaN(n) || n <= 0) {
      setError('Enter an amount greater than 0.');
      return;
    }
    setSaving(true);
    try {
      await depositCredits(slug, { credits: n, note: note.trim() || null });
      onDone();
    } catch {
      onError('Failed to deposit credits.');
      setError('Failed to deposit credits.');
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Deposit credits (admin)" size="sm">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error && (
          <div style={{ padding: '10px 14px', borderRadius: '8px', backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', color: '#F87171', fontSize: '13px' }}>
            {error}
          </div>
        )}
        <Input
          label="Amount (credits)"
          type="number"
          min={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="e.g. 100"
        />
        <Input
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Audit note"
        />
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Depositing…' : 'Deposit'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Set tier modal (admin) ─────────────────────────────────────────────────────

interface SetTierModalProps {
  channel: Channel;
  onClose: () => void;
  onDone: () => void;
  onError: (msg: string) => void;
}

function SetTierModal({ channel, onClose, onDone, onError }: SetTierModalProps) {
  const [tier, setTier] = useState<string>(channel.subscription_tier?.toString() ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    // PATCH /admin/channels/{channel_id} — requires the channel id.
    const channelId = (channel as { id?: string }).id;
    if (!channelId) {
      setError('Channel id is unavailable; cannot set tier.');
      return;
    }
    const parsedTier = tier === '' ? null : (parseInt(tier, 10) as 1 | 2 | 3);
    setSaving(true);
    try {
      await patchAdminChannel(channelId, {
        subscription_tier: parsedTier,
        // Setting a tier activates the subscription; clearing it clears status.
        subscription_status: parsedTier == null ? null : 'active',
      });
      onDone();
    } catch {
      onError('Failed to update subscription tier.');
      setError('Failed to update subscription tier.');
      setSaving(false);
    }
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    height: '40px',
    borderRadius: '10px',
    border: '1px solid var(--color-border-subtle)',
    backgroundColor: 'var(--color-dark-elevated-2)',
    color: 'var(--color-text-primary)',
    padding: '0 12px',
    fontSize: '14px',
    outline: 'none',
  };

  return (
    <Modal isOpen onClose={onClose} title="Set subscription tier (admin)" size="sm">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error && (
          <div style={{ padding: '10px 14px', borderRadius: '8px', backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', color: '#F87171', fontSize: '13px' }}>
            {error}
          </div>
        )}
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
            Subscription tier
          </label>
          <select value={tier} onChange={(e) => setTier(e.target.value)} style={selectStyle}>
            <option value="">No tier (clear)</option>
            <option value="1">Tier 1</option>
            <option value="2">Tier 2</option>
            <option value="3">Tier 3</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Save tier'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Coach cap modal ─────────────────────────────────────────────────────────

interface CoachCapModalProps {
  slug: string;
  coach: ChannelMember;
  currentCap: CoachCreditCap | null;
  onClose: () => void;
  onDone: () => void;
  onError: (msg: string) => void;
}

function CoachCapModal({ slug, coach, currentCap, onClose, onDone, onError }: CoachCapModalProps) {
  const [cap, setCap] = useState<string>(currentCap ? String(currentCap.allocation_cap) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    const n = parseInt(cap, 10);
    if (isNaN(n) || n < 0) {
      setError('Enter a cap of 0 or more.');
      return;
    }
    setSaving(true);
    try {
      await setCoachCreditCap(slug, coach.user_id, { allocation_cap: n });
      onDone();
    } catch {
      onError('Failed to update coach cap.');
      setError('Failed to update coach cap.');
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Allocation cap — ${memberLabel(coach)}`} size="sm">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error && (
          <div style={{ padding: '10px 14px', borderRadius: '8px', backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', color: '#F87171', fontSize: '13px' }}>
            {error}
          </div>
        )}
        <Input
          label="Allocation cap (0 = blocked from allocating)"
          type="number"
          min={0}
          value={cap}
          onChange={(e) => setCap(e.target.value)}
          placeholder="e.g. 50"
        />
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Save cap'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
